// Multi-Tenant Role System for QuickDial

import type { AccessGovernanceMode, TenantAdminSource } from "./models";

export type TenantRole = "TenantAdmin" | "Manager" | "User" | "Guest";
export type ResourceRole = "Owner" | "Manager" | "Subscriber" | "Viewer";

export interface MultiTenantUser {
  id: string;
  tenantId: string;
  userId: string; // Azure AD Object ID
  upn: string;
  name: string;
  
  // Effective customer-tenant role resolved by the API (/api/me) under the tenant's access-governance
  // mode: TenantAdmin may come from the Entra QuickDial.Admin app role, a stored app-managed role, or
  // both. It is never a HostingPortal claim; the portal checks raw Entra roles only.
  tenantRole: TenantRole;

  // Where the effective tenant-admin status comes from, as reported by /api/me.
  effectiveAdminSource?: TenantAdminSource;

  // The tenant's access-governance mode, as reported by /api/me.
  accessGovernanceMode?: AccessGovernanceMode;
  
  // Granular list-level manager flag (set by API based on list.managers array)
  isListManager?: boolean;
  
  // Resource-specific roles
  resourceRoles: {
    sites: Record<string, ResourceRole>;
    lists: Record<string, ResourceRole>;
  };
  
  // Tenant permissions
  permissions: {
    canManageUsers: boolean;
    canCreateSites: boolean;
    canCreateLists: boolean;
    canViewAllContacts: boolean;
  };
}

// Simplified role definitions
export const TENANT_ROLES = {
  TenantAdmin: {
    name: "Tenant Administrator",
    description: "Full access to all tenant resources",
    level: 100,
    permissions: ["*"]
  },
  Manager: {
    name: "Manager",
    description: "Can manage lists, sites, and contacts",
    level: 70,
    permissions: ["manage.sites", "manage.lists", "manage.contacts", "manage.users"]
  },
  User: {
    name: "Standard User",
    description: "Can create and use contact lists",
    level: 50,
    permissions: ["create.lists", "use.contacts", "subscribe.lists"]
  },
  Guest: {
    name: "Guest User",
    description: "Read-only access to shared resources",
    level: 10,
    permissions: ["read.contacts", "read.lists"]
  }
} as const;

// Role hierarchy and permissions
export const ROLE_HIERARCHY = {
  // Tenant-level roles (from Azure AD)
  TenantAdmin: {
    level: 100,
    inherits: ["User"],
    permissions: ["*"] // All permissions
  },
  User: {
    level: 50,
    inherits: ["Guest"],
    permissions: ["contacts.read", "contacts.write", "lists.subscribe"]
  },
  Guest: {
    level: 10,
    inherits: [],
    permissions: ["contacts.read"]
  },
  
  // Application-specific roles
  Admin: {
    level: 90,
    permissions: ["*"]
  },
  Manager: {
    level: 70,
    permissions: ["sites.manage", "lists.manage", "contacts.*"]
  },
  Subscriber: {
    level: 30,
    permissions: ["contacts.read", "lists.read", "lists.subscribe"]
  },
  Viewer: {
    level: 20,
    permissions: ["contacts.read", "lists.read"]
  }
} as const;

// Permission checking utilities
export function hasPermission(
  user: MultiTenantUser, 
  permission: string, 
  resourceType?: 'site' | 'list',
  resourceId?: string
): boolean {
  // Tenant Admin has all permissions
  if (user.tenantRole === "TenantAdmin") return true;
  
  // Check resource-specific permissions
  if (resourceType && resourceId) {
    const resourceRole = user.resourceRoles[`${resourceType}s` as keyof typeof user.resourceRoles]?.[resourceId];
    
    if (resourceRole === "Owner") return true;
    if (resourceRole === "Manager" && (permission.includes("manage") || permission.includes("write"))) return true;
    if (resourceRole === "Subscriber" && (permission.includes("read") || permission.includes("subscribe"))) return true;
    if (resourceRole === "Viewer" && permission.includes("read")) return true;
  }
  
  // Check tenant-level permissions
  switch (user.tenantRole) {
    case "Manager":
      return permission.includes("manage") || permission.includes("read") || permission.includes("write");
    case "User":
      return permission.includes("read") || permission.includes("write") || permission.includes("create");
    case "Guest":
      return permission.includes("read");
    default:
      return false;
  }
}

export function canEditContact(user: MultiTenantUser): boolean {
  return user.tenantRole === "TenantAdmin" || user.tenantRole === "Manager";
}

export function canManageList(user: MultiTenantUser, listId: string): boolean {
  if (user.tenantRole === "TenantAdmin") return true;
  const listRole = user.resourceRoles.lists[listId];
  return listRole === "Owner" || listRole === "Manager";
}